import { useMemo } from 'react';
import { Image } from '@ui/common/components/Image';
import { useSearchParams } from 'react-router';
import { DatePicker } from '@ui/common/components/DatePicker';
import { isMobileClient } from '@ui/utils/isMobileClient';
import { getMonth, getUTCDate } from '@ui/utils/date';

export type DateSwitcherProps = {
  matchDates?: string[];
};

export function DateSwitcher({ matchDates }: DateSwitcherProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = isMobileClient();
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');

  const selectedDate = useMemo(() => {
    const date = startDate ? new Date(startDate) : new Date();

    // Adjust for timezone offset to ensure correct date is displayed
    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate;
  }, [startDate]);

  const formattedDate = useMemo(() => {
    if (!startDate || startDate !== endDate) return null;
    return startDate; // Already in YYYY-MM-DD format from URL params
  }, [startDate, endDate]);

  const includedDates = useMemo(() => {
    if (!matchDates || matchDates.length === 0) {
      return undefined;
    }

    // Convert ISO date strings to Date objects
    return matchDates.map((dateStr) => {
      const date = new Date(dateStr);
      // Adjust for timezone offset to ensure correct date matching
      return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    });
  }, [matchDates]);

  const handleDateChange = (date: Date | null) => {
    if (!date) return;

    // Format date as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    // Update search params
    setSearchParams(
      (prev) => {
        prev.set('startDate', formattedDate);
        prev.set('endDate', formattedDate);
        return prev;
      },
      { replace: true }
    );
  };

  const handleClearDateFilter = () => {
    if (!startDate) return;

    // Extract the current month from the selected date using the same approach as FixturesPage
    const jsDate = new Date(startDate);
    const d = getUTCDate(jsDate.getFullYear(), jsDate.getMonth(), jsDate.getDate());
    const monthData = getMonth(d);

    // Update search params to show the entire month
    setSearchParams(
      (prev) => {
        prev.set('startDate', monthData.startDate);
        prev.set('endDate', monthData.endDate);
        return prev;
      },
      { replace: true }
    );
  };

  return (
    <div className="matches-list__date-switcher">
      {formattedDate && (
        <div className="matches-list__date-clear-group">
          {!isMobile && <span className="matches-list__selected-date">{formattedDate}</span>}
          <button
            className="matches-list__clear-date-button"
            onClick={handleClearDateFilter}
            aria-label="Clear date filter"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width={24} height={24}>
              <path
                fill="currentColor"
                d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"
              />
            </svg>
          </button>
        </div>
      )}
      <DatePicker
        selected={selectedDate}
        toggleCalendarOnIconClick
        onChange={handleDateChange}
        popperPlacement="bottom-end"
        includeDates={includedDates}
        customInput={
          <button className="matches-list__date-picker-button">
            <Image src="/calendar.svg" alt={'calendar'} width={24} height={24} />
          </button>
        }
      />
    </div>
  );
}
